import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { API_ROUTES, type AuthResponse, type SessionUser } from "@ziolo/shared";
import { apiRequest, setCsrfToken } from "../lib/apiClient.js";

interface SessionValue {
  user: SessionUser | null;
  /** True until the first `/auth/me` answer, so the login form does not flash. */
  restoring: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

/**
 * Holds the signed-in user for the whole app.
 *
 * The session itself is an HttpOnly cookie, so there is nothing to read on startup:
 * the only way to learn who we are is to ask the API. The CSRF token that comes back
 * with the answer is handed to the API client, which attaches it to every mutation.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [restoring, setRestoring] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    apiRequest<AuthResponse>(API_ROUTES.auth.me, { signal: controller.signal })
      .then((session) => {
        setCsrfToken(session.csrfToken);
        setUser(session.user);
      })
      .catch(() => {
        // A 401 here is the normal case for a visitor who is not signed in.
      })
      .finally(() => {
        if (!controller.signal.aborted) setRestoring(false);
      });

    return () => controller.abort();
  }, []);

  const authenticate = useCallback(
    async (path: string, username: string, password: string) => {
      const session = await apiRequest<AuthResponse>(path, {
        method: "POST",
        body: { username, password },
      });
      setCsrfToken(session.csrfToken);
      setUser(session.user);
    },
    [],
  );

  const value = useMemo<SessionValue>(
    () => ({
      user,
      restoring,
      login: (username, password) =>
        authenticate(API_ROUTES.auth.login, username, password),
      register: (username, password) =>
        authenticate(API_ROUTES.auth.register, username, password),
      logout: async () => {
        try {
          await apiRequest(API_ROUTES.auth.logout, { method: "POST" });
        } finally {
          // Drop the local session even if the call failed; the cookie may already
          // have expired, and leaving a stale user on screen is worse.
          setCsrfToken(null);
          setUser(null);
        }
      },
    }),
    [user, restoring, authenticate],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used inside a SessionProvider");
  }
  return context;
}
